import { forwardRef, useImperativeHandle } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Checkbox,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type { ProcessedFormField } from "@/types/api/formSchema";
import SubSectionHeading from "./SubSectionHeading";
import type { SchemaRendererHandle } from "./SchemaRenderer";
import { VendorApplicationSummary } from "../reviewer/VendorApplicationSummary";
import type { PriorStep } from "../reviewer/priorSteps";
import { maskMobile } from "../utils/formatting";

const BRAND_BLUE = "#3655F2";
const APPROVED_GREEN = "#1B7C4E";

const POLICIES_URL =
  "https://www.livspace.com/in/interiors/service/service-governance-policies";
const POLICIES = [
  {
    label: "Business Partner & Supplier Code of Conduct",
    hash: "#businessPartnerAmpSupplierCodeOfConduct",
  },
  {
    label: "Anti-Bribery and Anti-Corruption Policy",
    hash: "#antiBriberyAndAntiCorruptionPolicy",
  },
  {
    label: "Purchase Order — Terms and Conditions",
    hash: "#purchaseOrderTermsAndConditions",
  },
];

type Props = {
  declarationField: ProcessedFormField;
  priorSteps: PriorStep[];
  vendorMobile?: string | null;
  onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
};

export const ReviewAndSubmit = forwardRef<SchemaRendererHandle, Props>(
  function ReviewAndSubmit(
    { declarationField, priorSteps, vendorMobile, onSubmit },
    ref,
  ) {
    const form = useForm<{ declaration_accepted: boolean }>({
      defaultValues: { declaration_accepted: false },
      mode: "onSubmit",
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        form.clearErrors();
        void form.handleSubmit((data) =>
          onSubmit({ declaration_accepted: data.declaration_accepted }),
        )();
      },
      reset: () => form.reset(),
      getValues: () => form.getValues() as unknown as Record<string, unknown>,
      isDirty: () => form.formState.isDirty,
      markSaved: () => {
        form.reset(form.getValues(), {
          keepValues: true,
          keepDirty: false,
          keepDirtyValues: false,
        });
      },
      setFieldErrors: (errors) => {
        for (const [name, message] of Object.entries(errors)) {
          form.setError(name as "declaration_accepted", {
            type: "server",
            message,
          });
        }
      },
    }));

    const hasData = priorSteps.some(
      (s) =>
        Object.keys(s.data).length > 0 &&
        Object.values(s.data).some((v) => v !== "" && v != null),
    );

    return (
      <form onSubmit={form.handleSubmit((data) => onSubmit(data))}>
        {!hasData && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              borderColor: "#E0E4EF",
              bgcolor: "#FAFBFF",
              color: "text.secondary",
              fontSize: 13,
            }}
          >
            Previously-entered details are not available for preview yet.
            Please verify the declaration below and submit — the review team
            will see the full application.
          </Paper>
        )}

        <VendorApplicationSummary priorSteps={priorSteps} authMode="vendor" />


        <Box sx={{ mb: 3 }}>
          <SubSectionHeading>VERIFICATION</SubSectionHeading>
          <Table size="small" sx={{ "& td": { borderColor: "#E0E4EF" } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontSize: 13 }}>
                  OTP Verified
                  {vendorMobile && (
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", ml: 1 }}
                    >
                      ({maskMobile(vendorMobile)})
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: APPROVED_GREEN, fontWeight: 600 }}
                >
                  ✓ Yes
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderColor: "#E0E4EF",
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}
            >
              Please review the following policies before you accept:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {POLICIES.map((p) => (
                <Box component="li" key={p.hash} sx={{ fontSize: 13, lineHeight: 1.7 }}>
                  <Box
                    component="a"
                    href={`${POLICIES_URL}${p.hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: BRAND_BLUE, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    {p.label}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Controller
            control={form.control}
            name="declaration_accepted"
            rules={{
              validate: (v) =>
                declarationField.required && !v
                  ? "Please accept the declaration to submit"
                  : true,
            }}
            render={({ field, fieldState }) => (
              <Box data-field-name="declaration_accepted">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{
                      p: 0,
                      color: BRAND_BLUE,
                      "&.Mui-checked": { color: BRAND_BLUE },
                    }}
                  />
                  <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                    {declarationField.resolvedLabel}
                    {declarationField.required && (
                      <Box
                        component="span"
                        sx={{ color: "error.main", ml: 0.5 }}
                      >
                        *
                      </Box>
                    )}
                  </Typography>
                </Box>
                {fieldState.error?.message && (
                  <FormHelperText error sx={{ mt: 0.5, ml: 3.5 }}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        </Paper>
      </form>
    );
  },
);
